import { type ComputedRef, type Ref, computed, ref } from 'vue'
import { debugConsoleWarn } from '../utils/chatDebugLog'
import type { MessageWithReply } from '../types/messageTypes'
import type { ChatMessage } from '../types'
import type { PinConfirmOptions } from './usePinnedMessages'
import { useMessageSelection } from './useMessageSelection'
import { useNotifications } from './useNotifications'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'

export interface MessageActionsOptions {
  messages: ComputedRef<ChatMessage[]>
  chatId: Ref<number | string | undefined> | ComputedRef<number | string | undefined>
  getMessageId: (msgData: MessageWithReply, senderId: number | string) => string | number
  closeMessageMenu: () => void
  canPinMessages: ComputedRef<boolean>
  canForwardFromActiveChat: ComputedRef<boolean>
  isPinDialogMode: ComputedRef<'dialog' | 'group'>
  pinActorName: ComputedRef<string>
  isPinned: (eventId: string | null | undefined) => boolean
  pinMessage: (eventId: string, options?: PinConfirmOptions) => Promise<void>
  unpinMessage: (eventId: string, scope?: 'group' | 'dialog') => Promise<void>
  onReply: (message: MessageWithReply, senderId: number | string) => void
  onStartEdit: (messageId: string | number, msgData: MessageWithReply) => void
  onOpenProfile: (userId: string | number) => void
  onQuoteClick: (messageId: string | number, matrixEventId?: string) => void
}

/**
 * Действия над сообщением ChatLog: reply/edit/delete/pin/forward.
 * Состояние диалогов живёт здесь, сами диалоги — в template компонента.
 */
export function useMessageActions(options: MessageActionsOptions) {
  const {
    messages,
    chatId,
    getMessageId,
    closeMessageMenu,
    canPinMessages,
    canForwardFromActiveChat,
    isPinDialogMode,
    pinActorName,
    isPinned,
    pinMessage,
    unpinMessage,
    onReply,
    onStartEdit,
    onOpenProfile,
    onQuoteClick,
  } = options

  const store = useChatStore()
  const chatStoreCore = useChatStoreCore()
  const { showError } = useNotifications()

  const selection = useMessageSelection({
    chatId,
    canForward: canForwardFromActiveChat,
  })

  const handleReply = (msgData: MessageWithReply, senderId: number | string) => {
    const messageId = getMessageId(msgData, senderId)

    const fullMessage = messages.value.find(message =>
      String(getMessageId(message, message.senderId)) === String(messageId),
    )

    if (fullMessage) onReply(fullMessage, senderId)

    closeMessageMenu()
  }

  const handleEdit = (msgData: MessageWithReply, senderId: number | string) => {
    const messageId = getMessageId(msgData, senderId)

    onStartEdit(messageId, msgData)
    closeMessageMenu()
  }

  const isDeleteMessageDialogOpen = ref(false)
  const pendingDeleteMessage = ref<MessageWithReply | null>(null)
  const isDeletingMessage = ref(false)

  const handleDelete = (
    msgData: MessageWithReply,
    _senderId: number | string,
  ) => {
    pendingDeleteMessage.value = msgData
    isDeleteMessageDialogOpen.value = true
    closeMessageMenu()
  }

  const confirmDeleteMessage = async () => {
    if (!store.activeChat?.chat?.id || !pendingDeleteMessage.value) return

    const pending = pendingDeleteMessage.value
    const messageId = pending.matrix_event_id ?? pending.id
    if (messageId == null || messageId === '') return

    isDeletingMessage.value = true
    try {
      await chatStoreCore.deleteMessage(
        store.activeChat.chat.id,
        messageId,
        pending.matrix_event_id,
      )
      isDeleteMessageDialogOpen.value = false
      pendingDeleteMessage.value = null
    } catch (error) {
      showError(error, 'Delete message')
    } finally {
      isDeletingMessage.value = false
    }
  }

  const cancelDeleteMessage = () => {
    isDeleteMessageDialogOpen.value = false
    pendingDeleteMessage.value = null
  }

  const handleProfile = (
    msgData: MessageWithReply,
    senderId: number | string,
  ) => {
    let userId: string | number | null = null

    if (msgData.created_by?.id) userId = msgData.created_by.id

    if (!userId && msgData.created_by) {
      const createdByAny = msgData.created_by as any

      userId
        = createdByAny.uuid || createdByAny.user_id || createdByAny.user_uuid
    }

    if (!userId) {
      const msgAny = msgData as any

      userId = msgAny.senderId || msgAny.sender_id || msgAny.user_id
    }

    if (!userId) userId = senderId

    if (userId) {
      onOpenProfile(userId)
    } else {
      debugConsoleWarn('[ChatLog][handleProfile] Не удалось получить userId', {
        msgData,
        senderId,
      })
    }

    closeMessageMenu()
  }

  const isPinDialogOpen = ref(false)
  const pendingPinMessage = ref<MessageWithReply | null>(null)
  const pinForPeer = ref(true)

  const getPinPreview = (msgData: MessageWithReply) =>
    String(msgData.message || msgData.content || '').trim()
    || (msgData.attachments?.length ? 'Вложение' : '')

  const handlePinToggle = (msgData: MessageWithReply) => {
    const eventId = msgData.matrix_event_id
    if (!eventId || !canPinMessages.value) {
      return
    }

    closeMessageMenu()

    if (isPinned(eventId)) {
      void unpinMessage(eventId, isPinDialogMode.value).catch(error => showError(error, 'Unpin message'))
      return
    }

    if (isPinDialogMode.value === 'group') {
      void pinMessage(eventId, {
        pinForPeer: true,
        preview: getPinPreview(msgData),
        actorName: pinActorName.value,
      }).catch(error => showError(error, 'Pin message'))
      return
    }

    pendingPinMessage.value = msgData
    pinForPeer.value = true
    isPinDialogOpen.value = true
  }

  const confirmPinMessage = async () => {
    const msgData = pendingPinMessage.value
    const eventId = msgData?.matrix_event_id
    if (!eventId) {
      return
    }

    try {
      await pinMessage(eventId, {
        pinForPeer: pinForPeer.value,
        preview: getPinPreview(msgData),
        actorName: pinActorName.value,
      })

      isPinDialogOpen.value = false
      pendingPinMessage.value = null
    } catch (error) {
      showError(error, 'Pin message')
    }
  }

  const cancelPinMessage = () => {
    isPinDialogOpen.value = false
    pendingPinMessage.value = null
  }

  const handlePinnedSelect = (message: { id: number | string; matrix_event_id?: string }) => {
    onQuoteClick(message.id, message.matrix_event_id)
  }

  const handlePinnedUnpin = async (message: { matrix_event_id?: string }) => {
    if (!message.matrix_event_id || !canPinMessages.value) {
      return
    }

    try {
      await unpinMessage(message.matrix_event_id, isPinDialogMode.value)
    } catch (error) {
      showError(error, 'Unpin message')
    }
  }

  const isForwardDialogOpen = ref(false)
  const pendingForwardEventIds = ref<string[]>([])
  const forwardDialogRef = ref<{ setForwarding: (value: boolean) => void } | null>(null)

  const pendingForwardCount = computed(() => pendingForwardEventIds.value.length)

  const handleForward = (msgData: MessageWithReply) => {
    if (!msgData.matrix_event_id || !canForwardFromActiveChat.value) {
      return
    }

    pendingForwardEventIds.value = [msgData.matrix_event_id]
    isForwardDialogOpen.value = true
    closeMessageMenu()
  }

  const handleSelectMessage = (msgData: MessageWithReply) => {
    selection.enterSelection(msgData)
    closeMessageMenu()
  }

  const openForwardSelected = () => {
    const eventIds = selection.selectedEventIds.value
    if (!eventIds.length || !canForwardFromActiveChat.value) {
      return
    }

    pendingForwardEventIds.value = [...eventIds]
    isForwardDialogOpen.value = true
  }

  const cancelForwardMessage = () => {
    isForwardDialogOpen.value = false
    pendingForwardEventIds.value = []
    forwardDialogRef.value?.setForwarding(false)
  }

  const confirmForwardMessage = async (payload: {
    destinationChatId: number
    destinationTopicId?: number | null
  }) => {
    const eventIds = pendingForwardEventIds.value
    if (!eventIds.length) return

    forwardDialogRef.value?.setForwarding(true)
    try {
      await chatStoreCore.forwardMessages({
        sourceEventIds: eventIds,
        destinationChatId: payload.destinationChatId,
        destinationTopicId: payload.destinationTopicId,
      })
      isForwardDialogOpen.value = false
      pendingForwardEventIds.value = []
      selection.clearSelection()
    } catch (error) {
      showError(error, 'Forward message')
      forwardDialogRef.value?.setForwarding(false)
    }
  }

  const handleSystemMessageClick = (message: MessageWithReply) => {
    const targetId = message.system_target_event_id
    if (!targetId) {
      return
    }

    onQuoteClick(targetId, targetId)
  }

  return {
    handleReply,
    handleEdit,
    handleProfile,
    handleSystemMessageClick,
    isDeleteMessageDialogOpen,
    isDeletingMessage,
    handleDelete,
    confirmDeleteMessage,
    cancelDeleteMessage,
    isPinDialogOpen,
    pinForPeer,
    handlePinToggle,
    confirmPinMessage,
    cancelPinMessage,
    handlePinnedSelect,
    handlePinnedUnpin,
    isForwardDialogOpen,
    forwardDialogRef,
    pendingForwardCount,
    handleForward,
    handleSelectMessage,
    openForwardSelected,
    cancelForwardMessage,
    confirmForwardMessage,
    isSelectionMode: selection.isSelectionMode,
    selectedCount: selection.selectedCount,
    isMessageSelected: selection.isSelected,
    toggleMessageSelection: selection.toggleMessage,
    clearMessageSelection: selection.clearSelection,
  }
}
